import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { TextField } from 'components/Field/TextField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import deepmerge from 'deepmerge';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useSelector } from 'hooks/useSelector';
import { filterArrayEmpty } from 'lib/riskbase';
import { cloneDeep, isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK, doSetupOrganisationSave } from 'store/actions';
import {
  selectUserIsSupport,
  selectUserSelectedOrganisation,
} from 'store/auth/selectors';
import { selectSetupOrganisation } from 'store/remote/selectors';

export const OrganisationBillingModal: React.FC<
  ModalScreenProps<ModalScreens.SettingsOrganisationBillingModal>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const forceUpdate = useForceUpdate();
  const isSupport = useSelector(selectUserIsSupport);

  const organisation = useSelector(selectUserSelectedOrganisation);
  const setupOrganisation = useSelector(selectSetupOrganisation);
  const addressFieldKey = React.useRef(0);

  const initValues = React.useRef({
    name: route.params.name,
    address1: route.params.address1,
    address2: route.params.address2,
    city: route.params.city,
    county: route.params.county,
    postcode: route.params.postcode,
    emails: route.params.emails,
    emailsPo: route.params.emailsPo,
  });
  const changes = React.useRef(cloneDeep(initValues.current));

  const checkIsDirty = React.useCallback(
    () => !isEqual(initValues.current, changes.current),
    [],
  );
  const isDirty = React.useRef<boolean>(false);
  isDirty.current = checkIsDirty();

  const checkIsAddressDiff = React.useCallback(
    () =>
      organisation?.address.address1 !== changes.current.address1 ||
      organisation?.address.address2 !== changes.current.address2 ||
      organisation?.address.city !== changes.current.city ||
      organisation?.address.county !== changes.current.county ||
      organisation?.address.postcode !== changes.current.postcode,
    [
      organisation?.address.address1,
      organisation?.address.address2,
      organisation?.address.city,
      organisation?.address.county,
      organisation?.address.postcode,
    ],
  );
  const isAddressDiff = React.useRef<boolean>(false);
  isAddressDiff.current = checkIsAddressDiff();

  const handleChangeAddress = React.useCallback(
    (newChanges: Partial<typeof changes.current>) => {
      changes.current = deepmerge(changes.current, newChanges, {
        arrayMerge: (_, a) => a,
      });
      addressFieldKey.current += 1;
      if (
        isDirty.current !== checkIsDirty() ||
        isAddressDiff.current !== checkIsAddressDiff()
      ) {
        forceUpdate();
      }
    },
    [checkIsAddressDiff, checkIsDirty, forceUpdate],
  );

  const handleChangeWithName = React.useCallback(
    <
      N extends keyof typeof changes.current,
      V extends (typeof changes.current)[N],
    >(
      name: N,
      value: V,
    ) => {
      changes.current[name] = value;
      if (
        isDirty.current !== checkIsDirty() ||
        isAddressDiff.current !== checkIsAddressDiff()
      ) {
        forceUpdate();
      }
    },
    [checkIsAddressDiff, checkIsDirty, forceUpdate],
  );

  const handleSave = React.useCallback(() => {
    if (isDirty.current) {
      dispatch(
        doSetupOrganisationSave({
          changes: {
            billingAddress: {
              name: changes.current.name,
              address1: changes.current.address1,
              address2: changes.current.address2,
              city: changes.current.city,
              county: changes.current.county,
              postcode: changes.current.postcode,
            },
            billingEmails: changes.current.emails,
            billingEmailsPo: changes.current.emailsPo,
          },
        }),
      );
      return;
    }
    dispatch(
      NAVIGATE_MODAL_BACK([ModalScreens.SettingsOrganisationBillingModal]),
    );
  }, [dispatch]);

  const handleClose = React.useCallback(() => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: () => handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, handleSave, dispatch]);

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  if (!organisation || !setupOrganisation) {
    return null;
  }

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Billing"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
    >
      <Card label="Name">
        {isSupport ? (
          <TextField
            placeholder="..."
            value={changes.current.name}
            onChangeWithName={handleChangeWithName}
            name="name"
            autoCapitalize="words"
            autoCorrect={false}
            onEsc={handleClose}
            autoFocusWeb
            autoFocusDelay
          />
        ) : (
          <CardRow value={changes.current.name} />
        )}
      </Card>

      <Card
        label="Address"
        explanationLinkText={`Copy from details: ${[
          organisation.address.address1,
          organisation.address.address2,
          organisation.address.city,
          organisation.address.county,
          organisation.address.postcode,
        ]
          .filter(filterArrayEmpty)
          .join(', ')}`}
        onExplanationLinkPress={
          isAddressDiff.current
            ? () =>
                handleChangeAddress({
                  address1: organisation.address.address1,
                  address2: organisation.address.address2,
                  city: organisation.address.city,
                  county: organisation.address.county,
                  postcode: organisation.address.postcode,
                })
            : undefined
        }
      >
        <TextField
          placeholder="Address 1"
          value={changes.current.address1}
          name="address1"
          onChangeWithName={handleChangeWithName}
          autoCapitalize="words"
          autoCorrect={false}
          onEsc={handleClose}
          key={addressFieldKey.current}
        />
        <TextField
          placeholder="Address 2"
          value={changes.current.address2}
          name="address2"
          onChangeWithName={handleChangeWithName}
          autoCapitalize="words"
          autoCorrect={false}
          onEsc={handleClose}
          key={addressFieldKey.current}
        />
        <TextField
          placeholder="City"
          value={changes.current.city}
          name="city"
          onChangeWithName={handleChangeWithName}
          autoCapitalize="words"
          autoCorrect={false}
          onEsc={handleClose}
          key={addressFieldKey.current}
        />
        <TextField
          placeholder="County"
          value={changes.current.county}
          name="county"
          onChangeWithName={handleChangeWithName}
          autoCapitalize="words"
          autoCorrect={false}
          onEsc={handleClose}
          key={addressFieldKey.current}
        />
        <TextField
          placeholder="Postcode"
          value={changes.current.postcode}
          name="postcode"
          onChangeWithName={handleChangeWithName}
          autoCapitalize="words"
          autoCorrect={false}
          onEsc={handleClose}
          key={addressFieldKey.current}
        />
      </Card>

      <Card
        label="Emails"
        explanation="Comma separated list of emails. Invoices and billing updates will be sent to these addresses"
      >
        <TextAreaField
          placeholder="No Emails"
          value={changes.current.emails.split(',').join(', ')}
          name="emails"
          onChangeWithName={handleChangeWithName}
          autoCapitalize="none"
          autoCorrect={false}
          onEsc={handleClose}
          maxLength={512}
        />
      </Card>

      <Card
        label="PO Emails"
        explanation="Comma separated list of emails. Invoices with PO numbers will be sent to these addresses in addition to emails listed above"
      >
        <TextAreaField
          placeholder="No Emails"
          value={changes.current.emailsPo.split(',').join(', ')}
          name="emailsPo"
          onChangeWithName={handleChangeWithName}
          autoCapitalize="none"
          autoCorrect={false}
          onEsc={handleClose}
          maxLength={512}
        />
      </Card>
    </Page>
  );
};
